import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Home4Fill from '@/assets/icons/home_4_fill.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import type { AuthClient } from '@/contexts/auth';

import BreadcrumbMenu from './BreadcrumbMenu';
import BreadcrumbPagePath from './BreadcrumbPagePath';
import CageDropdownItems from './CageDropdownItems';
import ClientDropdownItems from './ClientDropdownItems';
import LocationDropdownItems from './LocationDropdownItems';
import PenStepper from './PenStepper';
import { BREADCRUMB_SEPARATOR } from './types';
import { useBreadcrumbEntries } from './useBreadcrumbEntries';
import { useBreadcrumbNavigation } from './useBreadcrumbNavigation';
import { useBreadcrumbOverflow } from './useBreadcrumbOverflow';
import { useBreadcrumbRoutes } from './useBreadcrumbRoutes';

import styles from './OptoBreadCrumb.module.scss';

export interface OptoBreadCrumbProps {
    client?: AuthClient;
    clients: AuthClient[];
    locationId?: number;
    cageId?: number;
    pagePath: string;
    showHomeButton?: boolean;
}

export default function OptoBreadCrumb(props: OptoBreadCrumbProps) {
    const { client, clients, locationId, cageId, pagePath, showHomeButton = true } = props;
    const { t, i18n } = useTranslation();

    const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
    const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
    const [cageDropdownOpen, setCageDropdownOpen] = useState(false);

    const {
        isLocationRoute,
        isCageRoute,
        showLocationsOnClientOverview,
        showCagesOnLocationOverview,
    } = useBreadcrumbRoutes(pagePath);

    const collapsibleOrder = useMemo(() => {
        const order: string[] = [];
        if (clients?.length) order.push('client');
        if (isLocationRoute || showLocationsOnClientOverview) order.push('location');
        if (isCageRoute || showCagesOnLocationOverview) order.push('cage');
        return order;
    }, [
        clients?.length,
        isLocationRoute,
        isCageRoute,
        showLocationsOnClientOverview,
        showCagesOnLocationOverview,
    ]);

    const {
        isLoading,
        currentLocation,
        setCurrentLocation,
        currentCage,
        penCohortName,
        clientsByStatus,
        locationsByStatus,
        cagesByStatus,
        setClientNameFilter,
    } = useBreadcrumbEntries({
        client,
        clients,
        locationId,
        cageId,
        showLocationsOnClientOverview,
        showCagesOnLocationOverview,
    });

    const placeholderText = `${t('molecules.optoBreadCrumb.filter')} ${t('common.labels.clients').toLowerCase()}`;

    // Demo mode needs no special case: `currentLocation` is derived from the masked
    // locations, so the cohort names it carries are already masked.
    const cageLabel = currentCage
        ? penCohortName
            ? `${currentCage.name} - ${penCohortName}`
            : currentCage.name
        : t('breadCrumb.component.cageLabel');

    const clientLabel = client?.name ? client.name : t('molecules.common.chooseClient');
    const locationLabel = currentLocation?.locationName ?? t('breadCrumb.component.siteLabel');

    const { containerRef, contentRef, sectionRefs, collapsedCount, collapsedSet } =
        useBreadcrumbOverflow(
            collapsibleOrder,
            [clientLabel, locationLabel, cageLabel, pagePath, i18n.language].join('\u0000')
        );

    const {
        handleHomeNavigation,
        handleClientNavigation,
        handleLocationNavigation,
        handleCageNavigation,
    } = useBreadcrumbNavigation({
        client,
        currentLocation,
        setCurrentLocation,
        pagePath,
        setClientNameFilter,
    });

    const handleClientDropdownOpenChange = (open: boolean) => {
        setClientDropdownOpen(open);
        setClientNameFilter('');
    };

    const cageMenu = (
        <BreadcrumbMenu
            label={cageLabel}
            direction="down"
            isOpen={cageDropdownOpen}
            onOpenChange={setCageDropdownOpen}
            disabled={isLoading}
            menuClassName={cageDropdownOpen ? styles.cages_dropdownMenu : ''}>
            <CageDropdownItems
                cagesByStatus={cagesByStatus}
                onCageClick={handleCageNavigation}
                selectedCageId={cageId}
            />
        </BreadcrumbMenu>
    );

    const penSwitch = (menu?: ReactNode) =>
        isCageRoute && cageId != null ? (
            <PenStepper
                className={styles.penSwitch}
                cagesByStatus={cagesByStatus}
                cageId={cageId}
                clientId={client?.id}
                locationId={currentLocation?.locationId}
                pagePath={pagePath}>
                {menu}
            </PenStepper>
        ) : null;

    return (
        <div ref={containerRef} className={styles.breadcrumbsContainer}>
            <div ref={contentRef} className={styles.breadcrumbsContent}>
                {clients && (
                    <>
                        {showHomeButton && (
                            <>
                                <Button
                                    variant="secondary"
                                    size="small"
                                    iconOnly
                                    aria-label={t('breadCrumb.component.home')}
                                    className={styles.homeButton}
                                    onClick={handleHomeNavigation}
                                    icon={
                                        <SvgIcon icon={Home4Fill} size="custom" customSize={20} />
                                    }
                                />
                                <span data-type="locationCageSeparator">
                                    {BREADCRUMB_SEPARATOR}
                                </span>
                            </>
                        )}

                        {collapsedCount > 0 && <span className={styles.ellipsis}>...</span>}

                        {!collapsedSet.has('client') && (
                            <div
                                ref={(el) => {
                                    sectionRefs.current.client = el;
                                }}>
                                <BreadcrumbMenu
                                    label={clientLabel}
                                    isOpen={clientDropdownOpen}
                                    onOpenChange={handleClientDropdownOpenChange}
                                    menuClassName={classNames({
                                        [styles.clients_dropdownMenu]: clientDropdownOpen,
                                        [styles.clients_dropdownMenuGrouped]:
                                            clientDropdownOpen &&
                                            clientsByStatus.active.length > 0 &&
                                            clientsByStatus.inactive.length > 0,
                                    })}>
                                    <ClientDropdownItems
                                        placeholderText={placeholderText}
                                        clientsByStatus={clientsByStatus}
                                        onClientClick={handleClientNavigation}
                                        onClientNameFilterChange={setClientNameFilter}
                                        selectedClient={client}
                                        isDropdownOpen={clientDropdownOpen}
                                        showSearch={clients.length > 1}
                                    />
                                </BreadcrumbMenu>
                            </div>
                        )}

                        {(isLocationRoute || showLocationsOnClientOverview) &&
                            !collapsedSet.has('location') && (
                                <div
                                    ref={(el) => {
                                        sectionRefs.current.location = el;
                                    }}
                                    className={styles.breadcrumbSection}>
                                    <span data-type="locationCageSeparator">
                                        {BREADCRUMB_SEPARATOR}
                                    </span>
                                    <BreadcrumbMenu
                                        label={locationLabel}
                                        isOpen={locationDropdownOpen}
                                        onOpenChange={setLocationDropdownOpen}
                                        disabled={isLoading}
                                        menuClassName={
                                            locationDropdownOpen
                                                ? styles.locations_dropdownMenu
                                                : ''
                                        }>
                                        <LocationDropdownItems
                                            locationsByStatus={locationsByStatus}
                                            onLocationClick={handleLocationNavigation}
                                            selectedLocationId={currentLocation?.locationId}
                                        />
                                    </BreadcrumbMenu>
                                </div>
                            )}

                        {(isCageRoute || showCagesOnLocationOverview) &&
                            !collapsedSet.has('cage') && (
                                <div
                                    ref={(el) => {
                                        sectionRefs.current.cage = el;
                                    }}
                                    className={styles.breadcrumbSection}>
                                    <span data-type="locationCageSeparator">
                                        {BREADCRUMB_SEPARATOR}
                                    </span>
                                    {isCageRoute && cageId != null ? penSwitch(cageMenu) : cageMenu}
                                </div>
                            )}
                        {collapsedSet.has('cage') && penSwitch()}
                    </>
                )}
                {pagePath && <BreadcrumbPagePath pagePath={pagePath} />}
            </div>
        </div>
    );
}
